import {ColorGenerator} from "@core/Util/colorGenerator";
import {G} from "@svgdotjs/svg.js";
import {supportsStatistics} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";
import formatNumber from "@core/Util/format-number";
import {makeBezier2, makeLine} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/curves";

const colorGenerator = new ColorGenerator();
/**
 * Calculate the center of the given points
 *
 * @param {Number[][]} points Array of points, each point is an array with an X at pos 0, and a Y at pos 1
 * @return {{x: number, y: number}}
 */
function calculateCenterCoordinates(points){
    const { min, max } = points.reduce((prev, [x, y]) => {
        prev.min.x = Math.min(prev.min.x, x);
        prev.max.x = Math.max(prev.max.x, x);
        prev.min.y = Math.min(prev.min.y, y);
        prev.max.y = Math.max(prev.max.y, y);

        return prev;
    }, {
        max: { x:-Infinity, y: -Infinity },
        min: { x: Infinity, y: Infinity }
    });

    return {
        x: min.x + Math.abs(max.x - min.x) / 2,
        y: min.y + Math.abs(max.y - min.y) / 2
    }
}

/**
 * Create SVG path instructions for drawing a curved line between the given points
 *
 * @param {Number[][]} points Array of points, each point is an array with an X at pos 0, and a Y at pos 1
 * @return {string}
 */
function createBrazierCurvePathFromPoints(points){
    let isFirst = true;

    let mpp = (pathGenFn, pathPoints) => {
        let data = pathGenFn(pathPoints)
        if(!isFirst) {
            // remove the move command as we start where the previous command ended
            data = data.substr(data.indexOf(' ', 2) + 1)
        }

        isFirst = false;
        return data;
    }

    // USE path instead of a  polygon for more advanced shapes
    return mpp(makeBezier2,[points[0],points[1]]) // Top left -> Top right
        +' '+ mpp(makeLine,[points[1], points[2]]) // Top right -> Brazier right
        +' '+ mpp(makeBezier2,[points[2], points[3]]) // Brazier right -> Brazier left
        +' z'; // close path
}

/**
 * Calculate the points required for creating a brazier curve between the given locations
 *
 * @param {JourneyBuilder.Sankey.PositioningInfo} from
 * @param {JourneyBuilder.Sankey.PositioningInfo} to
 * @return {Number[][]}
 */
function calculateBranchBrazierPoints(from, to){
    return [
        [from.x + from.width, from.y],                          // Top left
        [to.x, to.y],                                           // Top right
        [to.x, to.y + to.connectionHeight],                     // Brazier right
        [from.x + from.width, from.y + to.connectionHeight]     // Brazier left
    ];
}

/**
 * Apply a striped background to the given element
 * 
 * @param {SVG} svg
 * @param {G} g
 * @param {Rect} r
 */
function applyStripedBackground(svg, g, r){
    let stripeyBackground = svg.findOne('.striped-background');
    
    if(!stripeyBackground){
        stripeyBackground = svg.pattern(4, 4, add => {
            add.attr('patternUnits', 'userSpaceOnUse');
            add.path('M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2')
                .stroke({
                    color: '#000000',
                    width: 1
                });
        });
    }

    //SVG.js should generate an id for us; this should make it easier to have multiple charts on the same page
    g.add(r.clone().fill(`url(#${stripeyBackground.id()})`));
}

/**
 * Create an SVG G element for the given node using the given positionInfo
 *
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.SankeyNode} node
 * @param {Boolean} containsFilteredChildren
 * @param {JourneyBuilder.Sankey.SankeyNode} [parentNode]
 * @param {JourneyBuilder.Sankey.PositioningInfo} positionInfo
 * @returns {G}
 */
function createElementForNode(svg, node, containsFilteredChildren, parentNode, positionInfo) {
    const { x, y, width, elementHeight } = positionInfo;
    
    // console.log(positionInfo);
    
    var g = new G();
    var r = g.rect();
    r.size(width, elementHeight).move(x, y);

    if(containsFilteredChildren){
        applyStripedBackground(svg, g, r);
    }

    const nodeClasses = ['node', `node-${(node.type||'').toLowerCase()}`];
    if(node.isGoal){
        nodeClasses.push('goal');
    }
    
    if(!supportsStatistics(node)){
        nodeClasses.push('node-no-statistics');
    }

    //.stroke({ width: 1, color: 'black' });
    g.attr('data-nodetype', node.type || '')
        .attr('data-nodeid', node.id)
        .attr('data-amount', node.total)
        .attr('class', nodeClasses.join(' '))

    g.text(function(add) {
        let label = formatNumber(node.total);
        
        if(parentNode) {
            let percentage = 0;

            if(parentNode.total !== 0) {
                percentage = Math.round(node.total / (parentNode.total / 100));
            }

            if(isNaN(percentage)) percentage = 0;
            label += ` (${percentage}%)`;
        }

        //console.log('parent: ', container.parent)
        add.move(x + width + 2, y + 5 + 0.5 * elementHeight);
        add.attr('class', 'node-label');

        let txt = (node.label || '') + ': ';
        add.tspan(txt).attr('class','name'); // .newLine()
        add.tspan('' + label).attr('class','value')
    });
    
    return g;
}

/**
 * Render a connection line between node A and node B
 * 
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.SankeyNode} fromNode
 * @param {JourneyBuilder.Sankey.PositioningInfo} fromPos
 * @param {JourneyBuilder.Sankey.SankeyNode} toNode
 * @param {JourneyBuilder.Sankey.PositioningInfo} toPos
 */
function renderConnectionBetweenNodes(svg, fromNode,fromPos, toNode, toPos) {
    const points = calculateBranchBrazierPoints(fromPos, toPos);
    const g = new G();
    
    //Our text is positioned based on it's own width/height, for those properties to be calculated we need access to the boundingBox
    //the boundingBox only becomes available after adding our element to the DOM
    g.addTo(svg);
    g.path(createBrazierCurvePathFromPoints(points));

    //Make our connection identifiable/add metadata used for display purposes 
    g.attr('data-fromnode', fromNode.id)
        .attr('data-tonode', toNode.id)
        .attr('data-fromamount', fromNode.total || 0)
        .attr('data-toamount', toNode.total || 0)
        .attr('data-fromnodetype', fromNode.type)
        .attr('class', 'node-link');

    if(toNode.variation){
        g.fill(colorGenerator.generateSeededColor(toNode.variation))
            .attr('data-variation', toNode.variation)
            .addClass('variation-node-link');
    }

    g.text(function(add) {
        const center = calculateCenterCoordinates(points);

        let percentage = Math.round(toNode.total / (fromNode.total / 100));
        if(isNaN(percentage)) percentage = 0;
        
        add.attr('class', 'path-label')

        add.tspan(''+fromNode.label || '').attr('class','name')
        add.tspan(`▼ `).newLine();add.tspan(`${percentage}%`).attr('class','value')

        add.tspan(''+toNode.label || '').attr('class','name').newLine()


        const { width: textWidth, height: textHeight } = add.node.getBBox();
        
        add.move(
            center.x - (textWidth / 2),
            center.y - (textHeight / 2)
        );
    });
}

/**
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Map<String, JourneyBuilder.Sankey.PositioningInfo>} positionInfoPerNode
 */
function renderTouchPoints(svg, tree, positionInfoPerNode) {
    /** @type {Map<String, Number>} */
    const connectionYOffsetPerNode = new Map();
    
    const nodeElements = [];
    
    /** @type {Object<String, Number>} */
    const goalConnectionGroupingOffset = {};
    
    tree.forAllBranches(branch => {
        const node = branch.node;
        const containsFilteredChildren = branch.containsFilteredChildren;
        const parentNode = branch.parent && branch.parent.node;
        const positionInfo = positionInfoPerNode.get(node.id);

        nodeElements.push(createElementForNode(svg, node, containsFilteredChildren, parentNode, positionInfo));
        
        if(!branch.parent){
            return;
        }
        
        //Lines are stacked, our y offset starts at 0 -> gets increased with each new line
        const parentPosition = positionInfoPerNode.get(parentNode.id);
        const parentYOffset = connectionYOffsetPerNode.get(parentNode.id) || 0;
        
        //Goal nodes are stacked based on brickId, a single stack can be considered a single goal connections should therefore also stack
        let toY = positionInfo.y;
        if(node.isGoal){
            if(!goalConnectionGroupingOffset[node.brickId]){
                goalConnectionGroupingOffset[node.brickId] = positionInfo.y;
            }
            
            toY = goalConnectionGroupingOffset[node.brickId];
            goalConnectionGroupingOffset[node.brickId] += positionInfo.connectionHeight;
        }
        
        renderConnectionBetweenNodes(
            svg,
            parentNode,
            {
                ...parentPosition,
                y: parentPosition.y + parentYOffset
            },
            node,
          {
              ...positionInfo,
              y: toY
          }
        );

        connectionYOffsetPerNode.set(parentNode.id, positionInfo.connectionHeight + parentYOffset);
    });

    // all done, add the rectangles to the dom last, so they'll always be on top of the connections
    nodeElements.forEach(element => svg.add(element));
}

/**
 * Render lines between the different types/around the chart itself
 * @param {SVG} svg
 * @param {Object.<String, Number>} typeBaseYPositions
 */
function renderRaster(svg, typeBaseYPositions){
    const containerElement = new G();
    
    const { width: chartWidth, height: chartHeight } = svg.node.getBoundingClientRect();

    const drawVerticalLine =   (xPosition, strokeOptions) => drawLine(xPosition, 0, xPosition, chartHeight, strokeOptions);
    const drawHorizontalLine = (yPosition, strokeOptions) => drawLine(0, yPosition, chartWidth, yPosition, strokeOptions);

    const drawLine = (startX, startY, endX , endY, strokeOptions) => {
        containerElement.line(
            startX,
            startY,
            endX,
            endY
        ).stroke(strokeOptions);
    }

    //Draw a box around around our element
    drawVerticalLine(chartWidth - 1, {width: 1, color: '#E0E0E0'});
    drawHorizontalLine(chartHeight, {width: 1, color: '#E0E0E0'});

    drawVerticalLine(1, {width: 1, color: '#E0E0E0'});
    drawHorizontalLine(1, {width: 1, color: '#E0E0E0'});
    
    Object.values(typeBaseYPositions).forEach(y => {
        //Skip top most element to avoid overlap with box line which might make it appear a tad thicker
        if(y > 1){
            drawHorizontalLine(y, { color: '#F0F0F0', width: 0.75 });
        }
    })

    containerElement.addTo(svg);
}

export {
    renderTouchPoints,
    renderRaster
};